import { useState } from 'react';

const getInitialDisabled = (storageKey: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const stored = localStorage.getItem(storageKey);
    return stored === 'true';
  } catch (e) {
    return false;
  }
};

const getInitialSessionHidden = (storageKey: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const stored = sessionStorage.getItem(storageKey);
    return stored === 'true';
  } catch (e) {
    return false;
  }
};

export const useMockDetectorControl = (storagePrefix: string = '') => {
  const STORAGE_KEY_DISABLED = `${storagePrefix}mockdetector-permanently-disabled`;
  const STORAGE_KEY_SESSION_HIDDEN = `${storagePrefix}mockdetector-session-hidden`;
  
  const [isPermanentlyDisabled, setIsPermanentlyDisabled] = useState<boolean>(() => getInitialDisabled(STORAGE_KEY_DISABLED));
  const [isSessionHidden, setIsSessionHidden] = useState<boolean>(() => getInitialSessionHidden(STORAGE_KEY_SESSION_HIDDEN));

  const isVisible = !isPermanentlyDisabled && !isSessionHidden;

  // Permanently disable MockDetector (persists across sessions)
  const disablePermanently = () => {
    setIsPermanentlyDisabled(true);
    localStorage.setItem(STORAGE_KEY_DISABLED, 'true');
    console.log('🔴 MockDetector: Permanently disabled');
  };

  // Re-enable MockDetector
  const enable = () => {
    setIsPermanentlyDisabled(false);
    setIsSessionHidden(false);
    localStorage.removeItem(STORAGE_KEY_DISABLED);
    sessionStorage.removeItem(STORAGE_KEY_SESSION_HIDDEN);
    console.log('✅ MockDetector: Re-enabled');
  };

  // Hide for current session only (reappears after refresh)
  const hideForSession = () => {
    setIsSessionHidden(true);
    sessionStorage.setItem(STORAGE_KEY_SESSION_HIDDEN, 'true');
    console.log('👁️ MockDetector: Hidden for this session');
  };

  // Show for current session
  const showForSession = () => {
    setIsSessionHidden(false);
    sessionStorage.removeItem(STORAGE_KEY_SESSION_HIDDEN);
    console.log('👁️ MockDetector: Shown for this session');
  };

  // Get uninstall instructions
  const getUninstallInstructions = () => {
    return {
      npm: 'npm uninstall @mockdetector/widget',
      removeImport: 'Remove the import and <MockDetectorFloatingBubble /> from your code',
      alternative: 'Or use disablePermanently() to keep installed but disabled',
    };
  };

  return {
    isPermanentlyDisabled,
    isSessionHidden,
    isVisible,
    disablePermanently,
    enable,
    hideForSession,
    showForSession,
    getUninstallInstructions,
  };
};
